import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Package, CheckCircle, Truck, Home, Clock, ExternalLink, MapPin, MessageCircle, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADOS_ORDEN = [
    { key: 'nuevo', label: 'Pedido recibido', icon: Clock, descripcion: 'Estamos revisando tu pedido' },
    { key: 'confirmado', label: 'Confirmado', icon: CheckCircle, descripcion: 'Pedido confirmado. Preparando.' },
    { key: 'enviado', label: 'Enviado', icon: Truck, descripcion: 'Tu pedido ya va en camino' },
    { key: 'completado', label: 'Entregado', icon: Home, descripcion: 'Pedido entregado. ¡Gracias!' }
];

const formatFecha = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
            ' ' + d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
};

export const PedidoTracking = () => {
    const { codigo } = useParams();
    const [pedido, setPedido] = useState(null);
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [busqueda, setBusqueda] = useState('');

    useEffect(() => {
        const cargar = async () => {
            try {
                const cfg = await axios.get(`${API_URL}/api/tienda/config`);
                setConfig(cfg.data);
            } catch { /* no crash */ }
            if (codigo) {
                try {
                    const res = await axios.get(`${API_URL}/api/tienda/pedido/${codigo.toUpperCase()}`);
                    setPedido(res.data);
                } catch (e) {
                    if (e.response?.status === 404) {
                        toast.error('Codigo de pedido no encontrado');
                    } else {
                        toast.error('Error al consultar pedido');
                    }
                }
            }
            setLoading(false);
        };
        cargar();
    }, [codigo]);

    const buscarPedido = () => {
        const c = busqueda.trim().toUpperCase();
        if (!c) return;
        window.location.href = `/pedido/${c}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
            </div>
        );
    }

    // Sin pedido: vista de busqueda
    if (!pedido) {
        return (
            <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                    <Package className="w-16 h-16 mx-auto mb-4 text-amber-500" />
                    <h1 className="text-2xl font-bold text-zinc-900 mb-2">Consulta tu pedido</h1>
                    <p className="text-sm text-zinc-600 mb-6">Ingresa tu codigo de 6 caracteres</p>
                    <div className="flex gap-2 mb-4">
                        <input
                            type="text"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value.toUpperCase().slice(0, 6))}
                            placeholder="EJ. KJ7X4P"
                            className="flex-1 px-4 py-3 rounded-lg border border-zinc-300 text-center font-mono tracking-widest text-lg font-bold"
                            onKeyDown={(e) => e.key === 'Enter' && buscarPedido()}
                            data-testid="input-codigo-pedido"
                        />
                        <button
                            onClick={buscarPedido}
                            className="px-4 py-3 bg-amber-500 text-white rounded-lg font-bold hover:bg-amber-600 transition"
                            data-testid="btn-buscar"
                        >
                            <Search className="w-5 h-5" />
                        </button>
                    </div>
                    <Link to="/tienda" className="text-xs text-zinc-500 hover:text-amber-600">← Volver a la tienda</Link>
                </div>
            </div>
        );
    }

    // Pedido encontrado: timeline
    const cancelado = pedido.estado === 'cancelado';
    const currentIdx = cancelado ? -1 : ESTADOS_ORDEN.findIndex(e => e.key === pedido.estado);
    const waNumber = (config?.whatsapp_numero || '').replace(/\D/g, '');

    return (
        <div className="min-h-screen bg-zinc-50">
            {/* Header */}
            <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
                    <Link to="/tienda" className="flex items-center gap-2">
                        <img src="/logo-karry.png" alt="Karry Jean's" className="w-7 h-7 rounded object-contain" />
                        <span className="text-base font-bold tracking-wider text-zinc-900">KARRY JEAN'S</span>
                    </Link>
                    {waNumber && (
                        <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer"
                            className="text-xs px-3 py-1.5 rounded-full bg-emerald-500 text-white font-bold flex items-center gap-1.5">
                            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </a>
                    )}
                </div>
            </header>

            <div className="max-w-4xl mx-auto p-4 sm:p-6">
                {/* Card pedido */}
                <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 mb-4">
                    <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                        <div>
                            <p className="text-xs uppercase tracking-widest text-zinc-500">Codigo de pedido</p>
                            <p className="text-3xl font-extrabold font-mono tracking-widest text-zinc-900">{pedido.codigo_seguimiento}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-zinc-500">Fecha</p>
                            <p className="text-sm font-bold text-zinc-700">{formatFecha(pedido.created_at)}</p>
                        </div>
                    </div>
                    {cancelado && (
                        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm font-bold text-red-700">
                            Este pedido fue cancelado. Contacta por WhatsApp para mas informacion.
                        </div>
                    )}
                </div>

                {/* Timeline */}
                {!cancelado && (
                    <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 mb-4">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-4">Estado del pedido</h2>
                        <div className="space-y-0">
                            {ESTADOS_ORDEN.map((e, i) => {
                                const Icon = e.icon;
                                const isDone = i <= currentIdx;
                                const isCurrent = i === currentIdx;
                                return (
                                    <div key={e.key} className="flex gap-3 relative">
                                        <div className="flex flex-col items-center">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${isDone ? 'bg-emerald-500 text-white' : 'bg-zinc-200 text-zinc-400'} ${isCurrent ? 'ring-4 ring-emerald-200 animate-pulse' : ''}`}>
                                                <Icon className="w-5 h-5" />
                                            </div>
                                            {i < ESTADOS_ORDEN.length - 1 && (
                                                <div className={`w-0.5 flex-1 my-1 ${i < currentIdx ? 'bg-emerald-500' : 'bg-zinc-200'}`} style={{ minHeight: '32px' }} />
                                            )}
                                        </div>
                                        <div className="flex-1 pb-6">
                                            <p className={`text-sm font-bold ${isDone ? 'text-zinc-900' : 'text-zinc-400'}`}>{e.label}</p>
                                            <p className={`text-xs ${isDone ? 'text-zinc-600' : 'text-zinc-400'}`}>{e.descripcion}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Productos */}
                <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 mb-4">
                    <h2 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-3">Productos ({pedido.items?.length || 0})</h2>
                    <div className="space-y-2">
                        {pedido.items?.map((item, i) => (
                            <div key={i} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-2">
                                <div>
                                    <p className="font-bold text-zinc-800">{item.nombre}</p>
                                    <p className="text-xs text-zinc-500">
                                        {item.talla && <>Talla: <b>{item.talla}</b> &middot; </>}
                                        {item.tipo_compra && <>{item.tipo_compra} &middot; </>}
                                        Cantidad: <b>{item.cantidad}</b>
                                    </p>
                                </div>
                                <p className="font-bold text-zinc-900">${(item.subtotal || item.precio * item.cantidad || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-zinc-200 flex justify-between items-center">
                        <span className="text-sm text-zinc-600">Total aproximado</span>
                        <span className="text-2xl font-extrabold text-amber-600">${(pedido.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
                    </div>
                </div>

                {/* Datos envio */}
                {(pedido.metodo_envio || pedido.ciudad) && (
                    <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 mb-4">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-3">Datos de envio</h2>
                        <div className="text-sm space-y-1">
                            {pedido.metodo_envio && (
                                <p><span className="text-zinc-500">Metodo:</span> <b className="text-zinc-800 capitalize">{pedido.metodo_envio.replace('_', ' ')}</b></p>
                            )}
                            {pedido.cliente_nombre && (
                                <p><span className="text-zinc-500">Cliente:</span> <b className="text-zinc-800">{pedido.cliente_nombre}</b></p>
                            )}
                            {pedido.ciudad && (
                                <p><span className="text-zinc-500">Ciudad/Estado:</span> <b className="text-zinc-800">{pedido.ciudad}{pedido.estado_envio ? `, ${pedido.estado_envio}` : ''}</b></p>
                            )}
                            {pedido.direccion_envio && (
                                <p><span className="text-zinc-500">Direccion:</span> <span className="text-zinc-700">{pedido.direccion_envio}</span></p>
                            )}
                        </div>
                    </div>
                )}

                {/* Rastreo paqueteria */}
                {pedido.numero_rastreo && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 sm:p-6 mb-4">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-amber-700 mb-2 flex items-center gap-2">
                            <Truck className="w-4 h-4" /> Numero de rastreo
                        </h2>
                        <p className="text-2xl font-extrabold font-mono text-zinc-900 mb-2 break-all">{pedido.numero_rastreo}</p>
                        {pedido.paqueteria && (
                            <p className="text-sm text-amber-700"><b>Paqueteria:</b> {pedido.paqueteria}</p>
                        )}
                        {pedido.tracking_url && (
                            <a href={pedido.tracking_url} target="_blank" rel="noreferrer"
                                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg font-bold text-sm hover:bg-amber-600"
                                data-testid="btn-rastrear">
                                <ExternalLink className="w-4 h-4" /> Rastrear en {pedido.paqueteria || 'la paqueteria'}
                            </a>
                        )}
                    </div>
                )}

                {/* WebView del tracking (iframe) */}
                {pedido.tracking_url && (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
                        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
                            <span className="text-sm font-bold text-zinc-700">Vista previa del rastreo</span>
                            <a href={pedido.tracking_url} target="_blank" rel="noreferrer" className="text-xs text-amber-600 hover:underline flex items-center gap-1">
                                Abrir en pestana nueva <ExternalLink className="w-3 h-3" />
                            </a>
                        </div>
                        <iframe
                            src={pedido.tracking_url}
                            title="Tracking paqueteria"
                            style={{ width: '100%', height: '500px', border: 0 }}
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                        />
                    </div>
                )}

                {/* Tienda fisica */}
                {pedido.metodo_envio === 'tienda' && config?.tienda_direccion && (
                    <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 mb-4">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
                            <MapPin className="w-4 h-4" /> Pasaras por tu pedido a
                        </h2>
                        <p className="text-sm font-bold text-zinc-800">{config.tienda_direccion}</p>
                        {config.tienda_horarios && (
                            <p className="text-xs text-zinc-600 mt-1" style={{ whiteSpace: 'pre-line' }}>{config.tienda_horarios}</p>
                        )}
                        {config.google_maps_link && (
                            <a href={config.google_maps_link} target="_blank" rel="noreferrer"
                                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg font-bold text-sm hover:bg-amber-600">
                                <MapPin className="w-4 h-4" /> Como llegar
                            </a>
                        )}
                    </div>
                )}

                {/* Acciones */}
                <div className="flex flex-col sm:flex-row gap-2">
                    <Link to="/tienda" className="flex-1 px-4 py-3 rounded-lg bg-zinc-200 text-zinc-700 text-center font-bold text-sm hover:bg-zinc-300">
                        Volver a la tienda
                    </Link>
                    {waNumber && (
                        <a href={`https://wa.me/${waNumber}?text=${encodeURIComponent(`Hola, quiero saber sobre mi pedido ${pedido.codigo_seguimiento}`)}`}
                            target="_blank" rel="noreferrer"
                            className="flex-1 px-4 py-3 rounded-lg bg-emerald-500 text-white text-center font-bold text-sm hover:bg-emerald-600 flex items-center justify-center gap-2"
                            data-testid="btn-contactar-tracking">
                            <MessageCircle className="w-4 h-4" /> Contactar
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PedidoTracking;
